/**
 * Custom hooks for GitLab route
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  addRepositoryWebhook,
  gitlabRepositoryQueries,
  removeRepositoryWebhook,
} from '@/lib/queries/integrations/scm/gitlab';
import type { GitlabRepository } from '@/lib/types/gitlab';

import { useRepositorySearch } from './useRepositorySearch';

export const useWebhookMutations = (
  defaultProject: { id: string },
  refreshRepositories: () => void,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
) => {
  const addWebhookMutation = useMutation({
    mutationFn: (repositoryId: number) =>
      addRepositoryWebhook({ projectId: defaultProject.id, repositoryId }),
    onSuccess: () => {
      refreshRepositories();
    },
  });

  const removeWebhookMutation = useMutation({
    mutationFn: (repositoryId: number) =>
      removeRepositoryWebhook({ projectId: defaultProject.id, repositoryId }),
    onSuccess: () => {
      refreshRepositories();
    },
  });

  return { addWebhookMutation, removeWebhookMutation };
};

export const useRouteLogic = (
  defaultProject: { id: string },
  repositories: GitlabRepository[],
  queryClient: ReturnType<typeof useQueryClient>,
): {
  addWebhookMutation: ReturnType<
    typeof useWebhookMutations
  >['addWebhookMutation'];
  removeWebhookMutation: ReturnType<
    typeof useWebhookMutations
  >['removeWebhookMutation'];
  filteredRepositories: GitlabRepository[];
  repositorySearchQuery: string;
  setRepositorySearchQuery: (query: string) => void;
  refreshRepositories: () => void;
} => {
  const refreshRepositories = () => {
    queryClient.invalidateQueries(
      gitlabRepositoryQueries.list({
        projectId: defaultProject.id,
        page: 1,
        perPage: 50,
      }),
    );
  };

  const { addWebhookMutation, removeWebhookMutation } = useWebhookMutations(
    defaultProject,
    refreshRepositories,
  );
  const {
    repositorySearchQuery,
    setRepositorySearchQuery,
    filteredRepositories,
  } = useRepositorySearch(repositories);

  return {
    addWebhookMutation,
    removeWebhookMutation,
    filteredRepositories,
    repositorySearchQuery,
    setRepositorySearchQuery,
    refreshRepositories,
  };
};
